import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { readRingLinkParams } from '../ring.js';

// Ring cameras in Account (private beta, server/routes/ring.js). A keeper
// starts the link in the Ring app; Ring brings them back to
// /app/connect/ring?nonce=&time=, which opens this panel with the link to
// confirm while signed in. Shown only to accounts in the beta list (or to
// someone Ring sent here, who is told it is not open to them yet).
export function RingAccountPanel({ focus = false }) {
  const confirm = useConfirm();
  const [linkParams] = useState(() => readRingLinkParams());
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);
  const [cams, setCams] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const s = await api('/api/ring/status');
      setStatus(s);
      if (s.available && s.links?.length) {
        const c = await api('/api/ring/cameras').catch(() => null);
        setCams(c);
      } else {
        setCams(null);
      }
    } catch {
      setStatus({ available: false, links: [], failed: true });
    }
  };
  useEffect(() => {
    load();
    api('/api/auth/me')
      .then((r) => setMe(r.user || null))
      .catch(() => {});
  }, []);

  // The link address is used once: a reload must not offer it again.
  useEffect(() => {
    if (linkParams && typeof window !== 'undefined') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [linkParams]);

  const claim = async () => {
    setBusy(true);
    setErr('');
    try {
      await api('/api/ring/claim', { method: 'POST', body: linkParams });
      setMsg(
        'Linked. To watch a tank, open Tank, Equipment and devices, Ring camera, and pick the camera that shows it.'
      );
      await load();
    } catch (e) {
      setErr(e.message || 'Could not link Ring. Open TankSensei from the Ring app again.');
    } finally {
      setBusy(false);
    }
  };

  const forget = async (link) => {
    const ok = await confirm({
      title: 'Stop using Ring cameras here?',
      body: 'TankSensei forgets your Ring link and the cameras on your tanks. To end it on Ring’s side too, remove TankSensei in the Ring app afterwards.',
      confirmLabel: 'Stop using Ring',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr('');
    try {
      await api(`/api/ring/links/${link.id}`, { method: 'DELETE' });
      setMsg('Done. Now remove TankSensei on Ring’s side too, under My Apps in your Ring account.');
      await load();
    } catch (e) {
      setErr(e.message || 'Could not do that just now.');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  if (status.failed && linkParams) {
    return (
      <section className="ts-add-form ring-panel" style={{ marginTop: 12 }} aria-live="polite">
        <h3 className="font-heading ring-title">Ring cameras</h3>
        <p className="ts-hint">
          Could not reach TankSensei just now. Ring&apos;s link lasts 10 minutes, so try again soon.
        </p>
        <button
          type="button"
          className="ts-save ring-big"
          onClick={() => {
            setStatus(null);
            load();
          }}
        >
          Try again
        </button>
      </section>
    );
  }
  if (!status.available) {
    if (!linkParams) return null;
    return (
      <section className="ts-add-form ring-panel" style={{ marginTop: 12 }} aria-live="polite">
        <h3 className="font-heading ring-title">Ring cameras</h3>
        <p className="ts-hint">
          Ring cameras are in a small test with a few keepers, and this account is not in it yet.
          Write to us with the speech bubble at the top if you would like to join.
        </p>
      </section>
    );
  }
  const links = status.links || [];
  const linked = links.filter((l) => l.status === 'linked');
  const relink = links.filter((l) => l.status === 'needs_relink');

  return (
    <section
      className="ts-add-form ring-panel"
      style={{ marginTop: 12 }}
      aria-labelledby="ring-title"
      data-focus={focus ? 'true' : undefined}
    >
      <h3 id="ring-title" className="font-heading ring-title">
        Ring cameras <span className="ring-beta">Beta</span>
      </h3>

      {linkParams && !msg && (
        <div className="ring-confirm">
          <p>
            Link your Ring cameras to the TankSensei account{' '}
            <strong>{me?.email || 'you are signed in to'}</strong>?
          </p>
          <p className="ts-hint">
            Only the cameras you picked in the Ring app come over. You watch them live on a
            tank&apos;s page. TankSensei keeps no video; Ring may save it in your Ring history.
          </p>
          <button type="button" className="ts-save ring-big" onClick={claim} disabled={busy}>
            {busy ? 'Linking…' : 'Link Ring to this account'}
          </button>
        </div>
      )}

      {msg && (
        <p role="status" className="ring-ok">
          {msg}
        </p>
      )}
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}

      {msg.startsWith('Done.') && status.my_apps_url && (
        <a
          className="ts-chip ring-myapps"
          href={status.my_apps_url}
          target="_blank"
          rel="noreferrer"
        >
          Open My Apps on ring.com
        </a>
      )}

      {relink.length > 0 && (
        <p className="ts-hint ring-warn">
          Ring needs you to link again: in the Ring app, remove TankSensei, then add it again. Then
          pick the camera on each tank again.
        </p>
      )}

      {linked.length > 0 ? (
        <>
          {cams?.cameras?.length ? (
            <ul className="ring-cams">
              {cams.cameras.map((c) => (
                <li key={`${c.link_id}:${c.id}`}>
                  <span className="ring-cam-name">{c.name}</span>
                  <span className={`ring-cam-state${c.online === false ? ' dvh-quiet' : ''}`}>
                    {c.encrypted
                      ? 'End-to-end encrypted, so no other app can show its video'
                      : c.online === false
                        ? 'Offline'
                        : c.online
                          ? 'Online'
                          : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ts-hint">
              {cams?.problems?.some((p) => p.code === 'needs_relink')
                ? 'Ring needs you to link again: in the Ring app, remove TankSensei, then add it again. Then pick the camera on each tank again.'
                : cams?.problems?.length || !cams
                  ? 'Could not reach Ring just now. Try again in a minute.'
                  : 'No cameras shared yet. In the Ring app, pick which cameras TankSensei can see.'}
            </p>
          )}
          <p className="ts-hint">
            To watch, open a tank&apos;s Tank page, Equipment and devices, Ring camera.
          </p>
          {linked.map((l) => (
            <button
              key={l.id}
              type="button"
              className="ts-chip ts-chip-danger ring-forget"
              onClick={() => forget(l)}
              disabled={busy}
            >
              Stop using Ring here
            </button>
          ))}
        </>
      ) : (
        !linkParams && (
          <>
            <p className="ts-hint">
              Once we have added your Ring account to the test, TankSensei shows up in the Ring app
              for you:
            </p>
            <ol className="ring-steps">
              <li>In the Ring app, find TankSensei among the apps that work with Ring.</li>
              <li>Connect it and pick the camera that looks at your tank.</li>
              <li>Ring brings you back here to finish. You need a Ring plan or trial.</li>
            </ol>
            <p className="ts-hint">
              A camera with end-to-end encryption turned on in the Ring app never shows here: Ring
              lets no other app show its video.
            </p>
          </>
        )
      )}
    </section>
  );
}
